import React from 'react';

import { DialogActions, DialogContent } from '@mui/material';
import type { FormikConfig } from 'formik';
import { Formik } from 'formik';
import { AlineButton, AlineDialog, FormikAlineInput } from 'src/components';
import { makeStyles } from 'tss-react/mui';

export enum NameDialogVariant {
  ContractType = 'ContractType',
  CreateChartView = 'CreateChartView',
  CreateDocument = 'CreateDocument',
  CreateStorable = 'CreateStorable',
  CreateWorkflowView = 'CreateWorkflowView',
  RenameDocument = 'RenameDocument',
  RenameMetadataTag = 'RenameMetadataTag',
  RenameReport = 'RenameReport',
  RenameStorable = 'RenameStorable',
}

interface NameDialogProps {
  handleClose: () => void;
  onFormikSubmit: (name: string) => void;
  open: boolean;
  variant: NameDialogVariant;
  initialName?: string;
  placeholder?: string;
  validationSchema?: FormikConfig<{ name: string }>['validationSchema'];
}

const useStyles = makeStyles()(theme => ({
  container: {
    width: 348,
  },
  dialogContent: {
    padding: 0,
  },
  nameSection: {
    marginBottom: theme.spacing(3),
  },
  saveButton: {
    marginLeft: theme.spacing(1),
  },
}));

export function NameDialog(props: NameDialogProps): JSX.Element {
  const { classes } = useStyles();
  const {
    handleClose,
    initialName,
    onFormikSubmit,
    open,
    placeholder = '',
    validationSchema,
    variant,
  } = props;
  const { defaultInitialValue, submitText, title } = getNameDialogOriginTexts(variant);

  return (
    <AlineDialog
      classes={{ paperWidthMd: classes.container }}
      open={open}
      title={title}
      onClose={handleClose}
    >
      <Formik
        initialValues={{ name: initialName ?? defaultInitialValue }}
        // validateOnBlur={false} prevents some weird cases where input shows red in UI.  We only need to validate onSubmit/onChange anyways...
        validateOnBlur={false}
        validationSchema={validationSchema}
        onSubmit={({ name }) => onFormikSubmit(name)}
      >
        {({ dirty, handleReset, handleSubmit, isSubmitting }): JSX.Element => {
          return (
            <form onReset={handleReset} onSubmit={handleSubmit}>
              <DialogContent className={classes.dialogContent}>
                <FormikAlineInput
                  autoFocus
                  data-cy="name-storable-input"
                  fieldName="name"
                  placeholder={placeholder}
                />
              </DialogContent>
              <DialogActions>
                <AlineButton color="primary" variant="outlined" onClick={handleClose}>
                  Cancel
                </AlineButton>
                <AlineButton
                  className={classes.saveButton}
                  color="primary"
                  disabled={!dirty}
                  loading={isSubmitting}
                  type="submit"
                  variant="contained"
                >
                  {submitText}
                </AlineButton>
              </DialogActions>
            </form>
          );
        }}
      </Formik>
    </AlineDialog>
  );
}

function getNameDialogOriginTexts(variant: NameDialogVariant): {
  defaultInitialValue: string;
  submitText: string;
  title: string;
} {
  switch (variant) {
    case NameDialogVariant.CreateStorable:
    case NameDialogVariant.CreateDocument:
      return {
        defaultInitialValue: '',
        submitText: 'Create',
        title: 'New document',
      };
    case NameDialogVariant.CreateChartView:
      return { defaultInitialValue: '', submitText: 'Create', title: 'New chart view' };
    case NameDialogVariant.CreateWorkflowView:
      return { defaultInitialValue: '', submitText: 'Save', title: 'Save view' };
    case NameDialogVariant.ContractType:
      return { defaultInitialValue: '', submitText: 'Save', title: `Name contract type` };
    case NameDialogVariant.RenameMetadataTag:
      return { defaultInitialValue: '', submitText: 'Save', title: `Name tag` };
    case NameDialogVariant.RenameReport:
      return { defaultInitialValue: '', submitText: 'Save', title: 'Name report' };
    case NameDialogVariant.RenameStorable:
    case NameDialogVariant.RenameDocument:
      return { defaultInitialValue: '', submitText: 'Save', title: 'Name document' };
  }
}
